import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import ornament from "@/assets/ornament.png";
import roastPoster from "@/assets/sunday-roast.jpg";
import pastryPoster from "@/assets/pastry.jpg";

const EASE = [0.16, 1, 0.3, 1];

const posters = [
  {
    src: roastPoster,
    alt: "Sunday Roast poster — free dessert with every Sunday Roast",
    caption: "Sunday Roast — free dessert with every roast",
  },
  {
    src: pastryPoster,
    alt: "Pastry poster — buy a hot drink, get a pastry for £2",
    caption: "Buy a hot drink — get a pastry for £2",
  },
];

const lines = [
  { text: "New", cls: "text-cream" },
  { text: "website", cls: "text-cream" },
  { text: "coming soon.", cls: "italic text-sage" },
];

export default function Hero() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % posters.length), 5500);
    return () => clearInterval(t);
  }, []);

  return (
    <header className="relative flex min-h-screen flex-col overflow-hidden">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 left-1/2 h-[60vh] w-[80vw] -translate-x-1/2 rounded-full bg-sage/5 blur-3xl"
      />

      {/* top bar */}
      <div className="relative z-10 mx-auto flex w-full max-w-7xl items-center justify-between px-6 pt-7 md:px-12">
        <a href="#top" className="flex items-center gap-3" data-testid="brand-lockup">
          <img src={ornament} alt="" className="h-9 w-9 object-contain" />
          <span className="font-wordmark text-sm tracking-[0.35em] text-cream">THE MOON</span>
          <span className="mt-0.5 hidden font-sans text-[10px] tracking-[0.5em] text-sage-dim sm:block">
            HARROW
          </span>
        </a>
        <a
          href="#order"
          data-testid="nav-order-link"
          className="group flex items-center gap-2 rounded-full border border-line px-5 py-2.5 font-sans text-xs uppercase tracking-[0.2em] text-sage transition-colors duration-300 hover:border-sage/60 hover:text-cream"
        >
          Order on Deliveroo
          <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </a>
      </div>

      <div
        id="top"
        className="relative z-10 mx-auto grid w-full max-w-7xl flex-1 items-center gap-12 px-6 pb-20 pt-14 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16 md:px-12"
      >
        {/* left — the reveal */}
        <div>
          <h1 className="font-serif text-[clamp(3.2rem,8vw,6rem)] font-medium leading-[1.02]">
            {lines.map((l, i) => (
              <span key={l.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${l.cls}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.1, ease: EASE, delay: 0.35 + i * 0.16 }}
                >
                  {l.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 0.95 }}
            className="mt-7 max-w-md font-sans text-base font-light leading-relaxed text-cream-dim/85 md:text-lg"
          >
            Our new website is on its way. Until then, the cafe is open as ever — and every
            favourite can come straight to your door.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 1.1 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#order"
              data-testid="hero-cta-deliveroo"
              className="inline-flex items-center gap-3 rounded-full bg-cream px-8 py-4 font-sans text-xs uppercase tracking-[0.25em] text-ink transition-colors duration-300 hover:bg-sage"
            >
              Order on Deliveroo
            </a>
            <a
              href="#visit"
              data-testid="hero-cta-find-us"
              className="inline-flex items-center gap-3 rounded-full border border-line px-8 py-4 font-sans text-xs uppercase tracking-[0.25em] text-sage transition-colors duration-300 hover:border-sage/60 hover:text-cream"
            >
              Find us
              <ArrowDown className="h-3.5 w-3.5" />
            </a>
          </motion.div>
        </div>

        {/* right — rotating promotions */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease: EASE, delay: 0.9 }}
          className="w-full"
          data-testid="hero-poster-rotator"
        >
          <div className="relative border border-line bg-ink2 p-3 shadow-[0_40px_80px_-40px_rgba(0,0,0,0.8)] md:p-4">
            <div className="relative aspect-[3/2] overflow-hidden">
              {posters.map((p, i) => (
                <motion.img
                  key={p.caption}
                  src={p.src}
                  alt={p.alt}
                  data-testid={`hero-poster-${i}`}
                  className="absolute inset-0 h-full w-full object-cover"
                  initial={false}
                  animate={{
                    opacity: i === index ? 1 : 0,
                    scale: i === index ? 1 : 1.04,
                  }}
                  transition={{ duration: 1.4, ease: "easeInOut" }}
                />
              ))}
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between gap-4">
            <div className="min-h-[2.5rem] font-sans text-xs uppercase tracking-[0.22em] text-sage">
              <AnimatePresence mode="wait">
                <motion.span
                  key={posters[index].caption}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.5 }}
                  className="block"
                >
                  {posters[index].caption}
                </motion.span>
              </AnimatePresence>
            </div>
            <div className="flex shrink-0 gap-2">
              {posters.map((_, i) => (
                <button
                  key={i}
                  data-testid={`hero-poster-dot-${i}`}
                  aria-label={`Show promotion ${i + 1}`}
                  onClick={() => setIndex(i)}
                  className={`h-1.5 rounded-full transition-all duration-500 ${
                    i === index ? "w-8 bg-sage" : "w-3 bg-line hover:bg-sage-dim"
                  }`}
                />
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </header>
  );
}
